'use client'

import { useState } from 'react'
import { FileText, Send, Check, Plus, X } from 'lucide-react'
import MasjidPhotosField from '../../../components/onboarding/MasjidPhotosField'
import RichTextEditor from '../../../components/RichTextEditor'

/* PART 4 — contribute a record. At least one source link is REQUIRED; the form will
   not submit without one. Multiple sources + an optional historical photo are supported.
   Submissions go to the admin review queue, never live. */
export default function SuggestRecordForm() {
  const [form, setForm] = useState({ name: '', siteType: 'masjid', locality: '', city: '', approxEstablished: '', approxShaheed: '', summary: '', sourceNote: '', contact: '' })
  const [sourceUrls, setSourceUrls] = useState<string[]>([''])
  const [photos, setPhotos] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState('')
  const [error, setError] = useState('')
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }))

  const setUrl = (i: number, v: string) => setSourceUrls((a) => a.map((u, j) => (j === i ? v : u)))
  const addUrl = () => setSourceUrls((a) => (a.length >= 6 ? a : [...a, '']))
  const removeUrl = (i: number) => setSourceUrls((a) => (a.length <= 1 ? a : a.filter((_, j) => j !== i)))

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const cleaned = sourceUrls.map((u) => u.trim()).filter(Boolean)
    if (!cleaned.some((u) => /^https?:\/\/\S+$/i.test(u))) {
      setError('At least one valid source link (https://…) is required.')
      return
    }
    setBusy(true)
    try {
      const r = await fetch('/api/shaheed/suggest', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, sourceUrls: cleaned, photoUrls: photos }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) { setError(d.error || 'Could not submit right now.'); return }
      setDone(d.message || 'Submitted for review.')
    } catch { setError('Could not submit right now.') }
    finally { setBusy(false) }
  }

  if (done) return <div className="srf-done"><Check size={18} /> {done}<style>{css}</style></div>

  return (
    <form className="srf" onSubmit={submit}>
      <div className="srf-grid">
        <label className="srf-wide"><span>Masjid name *</span><input required value={form.name} onChange={(e) => set('name', e.target.value)} maxLength={160} /></label>
        <label><span>Locality</span><input value={form.locality} onChange={(e) => set('locality', e.target.value)} maxLength={120} /></label>
        <label><span>City</span><input value={form.city} onChange={(e) => set('city', e.target.value)} maxLength={120} /></label>
        {/* Site type — is this a shaheed masjid or a shaheed dargah? Carried through moderation onto
            the created record. Defaults to Masjid. */}
        <label><span>Site type</span>
          <select value={form.siteType} onChange={(e) => set('siteType', e.target.value)}>
            <option value="masjid">Masjid</option>
            <option value="dargah">Dargah</option>
          </select>
        </label>
        <label><span>Established (approx. year)</span><input value={form.approxEstablished} onChange={(e) => set('approxEstablished', e.target.value)} placeholder="e.g. 1870" maxLength={20} /></label>
        <label><span>Shaheed (approx. year)</span><input value={form.approxShaheed} onChange={(e) => set('approxShaheed', e.target.value)} placeholder="e.g. 1992" maxLength={20} /></label>
        {/* Rich text (same editor as the registration forms — bold/underline/highlight/colour).
            The server sanitizes on write (/api/shaheed/suggest → sanitize-richtext); approve
            copies the sanitized HTML into masjids.historical_summary, which the archive already
            renders as sanitized rich text. */}
        <div className="srf-wide">
          <span className="srf-lbl">Brief summary</span>
          <RichTextEditor value={form.summary} onChange={(html) => set('summary', html)} placeholder="A few factual lines about the masjid…" ariaLabel="Brief summary" />
        </div>

        {/* Repeatable source links — at least one required. */}
        <div className="srf-wide srf-sources">
          <span className="srf-lbl">Source links * <em>(gazette, court order, news archive, government document)</em></span>
          {sourceUrls.map((u, i) => (
            <div key={i} className="srf-srcrow">
              <input type="url" value={u} onChange={(e) => setUrl(i, e.target.value)} placeholder="https://…" maxLength={600} required={i === 0} />
              {sourceUrls.length > 1 && <button type="button" className="srf-iconbtn" onClick={() => removeUrl(i)} aria-label="Remove this source"><X size={14} /></button>}
            </div>
          ))}
          {sourceUrls.length < 6 && <button type="button" className="srf-add" onClick={addUrl}><Plus size={13} /> Add another source</button>}
        </div>

        <label className="srf-wide"><span>Note about the source(s)</span><input value={form.sourceNote} onChange={(e) => set('sourceNote', e.target.value)} maxLength={400} /></label>

        {/* Optional historical photos — reuses the Cloudinary upload flow (/api/upload). */}
        <div className="srf-wide">
          <span className="srf-lbl">Historical photos <em>(optional, up to 10)</em></span>
          <MasjidPhotosField value={photos} onChange={setPhotos} max={10} />
        </div>

        <label className="srf-wide"><span>Your contact (optional)</span><input value={form.contact} onChange={(e) => set('contact', e.target.value)} maxLength={160} /></label>
      </div>
      <p className="srf-note"><FileText size={13} /> Every record is evidence-governed. Without at least one source link, a record cannot be reviewed or added.</p>
      {error && <p className="srf-error">{error}</p>}
      <button type="submit" disabled={busy}><Send size={14} /> {busy ? 'Submitting…' : 'Submit for review'}</button>
      <style>{css}</style>
    </form>
  )
}

const css = `
  .srf{display:grid;gap:14px}
  .srf-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .srf-wide{grid-column:1 / -1}
  .srf label{display:grid;gap:5px}
  .srf label span,.srf-lbl{font-size:11.5px;font-weight:600;color:#b8b2a2;letter-spacing:.02em}
  .srf label span em,.srf-lbl em{font-style:normal;font-weight:400;color:#8c8674}
  .srf input,.srf textarea{background:rgba(255,255,255,.04);border:1px solid rgba(201,168,74,.25);border-radius:9px;padding:10px 12px;color:#E7E3D6;font:inherit}
  .srf input::placeholder,.srf textarea::placeholder{color:#6f6a5c}
  .srf-sources{display:grid;gap:8px}
  .srf-srcrow{display:flex;gap:8px;align-items:center}
  .srf-srcrow input{flex:1}
  .srf-iconbtn{display:inline-flex;align-items:center;justify-content:center;width:34px;height:38px;flex:0 0 auto;background:rgba(255,255,255,.04);border:1px solid rgba(201,168,74,.25);border-radius:9px;color:#C9A84A;cursor:pointer}
  .srf-add{justify-self:start;display:inline-flex;align-items:center;gap:5px;background:transparent;border:1px dashed rgba(201,168,74,.4);color:#C9A84A;border-radius:9px;padding:7px 12px;font-size:12px;font-weight:600;cursor:pointer}
  .srf-note{display:flex;align-items:center;gap:7px;font-size:12px;color:#b8b2a2;margin:0}
  .srf-error{color:#E0A0A0;font-size:12.5px;margin:0}
  .srf button[type=submit]{justify-self:start;display:inline-flex;align-items:center;gap:7px;background:#C9A84A;color:#1b1d22;border:0;border-radius:99px;padding:10px 20px;font-weight:700;font-size:13px;cursor:pointer}
  .srf button[type=submit]:disabled{opacity:.6}
  .srf-done{display:flex;align-items:center;gap:9px;color:#C9A84A;font-size:14px;padding:18px;background:rgba(201,168,74,.08);border:1px solid rgba(201,168,74,.25);border-radius:12px}
  @media(max-width:640px){.srf-grid{grid-template-columns:1fr}}
`
